import React from "react";
import { Row } from "react-bootstrap";
import { ILayers } from "../../../../../shared/interfaces/ILayers";
import {
    IGrpZoneamento,
    IProperty,
    IPropertyGeom,
} from "../../../../../shared/interfaces/IProperties";
import { Croqui } from "../../../bci/croquis/map";
import { CPAraucariaMapContainer } from "../../../bci/croquis/styles";
import { Divisor } from "../../../bci/styles";
import { getPaddingBasedOnSize } from "../../helpers/getPaddingBasedOnSize";

interface ZoneamentoProps {
    propertyInfo?: IProperty;
    propertyGeom?: IPropertyGeom;
    layers?: ILayers[];
    zoneamentos?: IGrpZoneamento[];
}

export const AraucariaZoneamento: React.FC<ZoneamentoProps> = ({
    propertyInfo,
    propertyGeom,
    layers,
    zoneamentos,
}) => {
    const validZoneamentos =
        zoneamentos?.filter((z) => Boolean(z && z.id && z.guia)) || [];
    const hasZoneamentos = validZoneamentos.length > 0;
    const hasPropertyZoneamento = propertyInfo?.grp_zoneamento;
    const isAppZone =
        propertyInfo?.zoneamento?.zona === "APP" ||
        propertyInfo?.zoneamento?.zona_uso === "APP";

    return (
        <>
            <Row>
                <table className="table table-striped">
                    <tbody>
                        {propertyInfo?.inscricaoimobiliaria && (
                            <tr>
                                <td>Inscrição Imobiliária:</td>
                                <td>{propertyInfo.inscricaoimobiliaria}</td>
                                {propertyGeom && layers && (
                                    <td rowSpan={8}>
                                        <div className="d-flex justify-content-center row">
                                            <CPAraucariaMapContainer>
                                                <Croqui
                                                    layers={layers}
                                                    propertyGeom={propertyGeom}
                                                    padding={getPaddingBasedOnSize(
                                                        propertyInfo.areaterreno
                                                    )}
                                                />
                                            </CPAraucariaMapContainer>
                                        </div>
                                    </td>
                                )}
                            </tr>
                        )}
                        <tr>
                            <td>Data:</td>
                            <td>{new Date().toLocaleDateString("pt-BR")}</td>
                        </tr>
                        {propertyInfo?.logradouronome && (
                            <tr>
                                <td>Endereço:</td>
                                <td>{propertyInfo.logradouronome}</td>
                            </tr>
                        )}
                        {propertyInfo?.logradouronumero && (
                            <tr>
                                <td>Número:</td>
                                <td>{propertyInfo.logradouronumero}</td>
                            </tr>
                        )}
                        {propertyInfo?.cep && (
                            <tr>
                                <td>CEP:</td>
                                <td>{propertyInfo.cep}</td>
                            </tr>
                        )}
                        {propertyInfo?.bairronome && (
                            <tr>
                                <td>Bairro:</td>
                                <td>{propertyInfo.bairronome}</td>
                            </tr>
                        )}
                        {propertyInfo?.quadra && (
                            <tr>
                                <td>Quadra:</td>
                                <td>{propertyInfo.quadra}</td>
                            </tr>
                        )}
                        {propertyInfo?.lote && (
                            <tr>
                                <td>Lote:</td>
                                <td>{propertyInfo.lote}</td>
                            </tr>
                        )}
                        {hasPropertyZoneamento && (
                            <tr>
                                <td>Zoneamento:</td>
                                <td colSpan={2}>
                                    <table className="table table-bordered">
                                        <thead>
                                            <tr>
                                                <th>Zoneamento</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>
                                                    {propertyInfo.grp_zoneamento?.guia.toUpperCase()}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                        )}
                        {hasZoneamentos ? (
                            <tr>
                                <td>Zoneamento:</td>
                                <td colSpan={2}>
                                    <table className="table table-bordered">
                                        <thead>
                                            <tr>
                                                <th>Descrição</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {validZoneamentos.map((zoneamento) => (
                                                <tr key={zoneamento.id}>
                                                    <td>{zoneamento.guia}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                        ) : (
                            <tr>
                                <td>Zoneamento:</td>
                                <td colSpan={2}>
                                    <div
                                        style={{
                                            padding: "10px 14px",
                                            backgroundColor: "#fffdf0",
                                            border: "1px solid #e0d39e",
                                            borderRadius: "4px",
                                            fontSize: "0.92rem",
                                        }}
                                    >
                                        <strong style={{ color: "#856404" }}>
                                            {propertyInfo?.zoneamento?.zona
                                                ? `Zona: ${propertyInfo.zoneamento.zona}`
                                                : "Zoneamento não parametrizado"}
                                        </strong>
                                        <div
                                            style={{
                                                fontSize: "0.85rem",
                                                color: "#555",
                                                marginTop: "4px",
                                            }}
                                        >
                                            {isAppZone
                                                ? "Imóvel registrado com Área de Preservação Permanente (APP) / restrição ambiental. Diretrizes de ocupação e viabilidade sujeitas à análise técnica da Secretaria Municipal de Urbanismo."
                                                : "Parâmetros não tabelados na WebGeo para esta zona. Consulte a Secretaria Municipal de Urbanismo para análise presencial."}
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                {hasZoneamentos &&
                    validZoneamentos
                        .filter(
                            (zoneamento) => zoneamento.relatorio === "ZR1-C"
                        )
                        .map(() => (
                            <p
                                key="alerta-zr1c"
                                className="text-center"
                                style={{
                                    fontWeight: "bold",
                                    color: "red",
                                    textTransform: "uppercase",
                                }}
                            >
                                ALERTA: O imóvel está localizado dentro dos
                                limites da cota de recorrência de cheias.
                            </p>
                        ))}
                {(propertyInfo?.zoneamento?.lei_cnaes_permitidos === "SIM" || isAppZone) && (
                    <p
                        className="text-center"
                        style={{
                            fontWeight: "bold",
                            color: "#1e7e34",
                            textTransform: "uppercase",
                            marginTop: "6px",
                        }}
                    >
                        ALERTA: O imóvel possui restrição de Área de Preservação Permanente (APP).
                    </p>
                )}
            </Row>
            <Divisor />
        </>
    );
};
